import { useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  IS_CAPACITOR,
  IS_CHROME_EXTENSION,
  IS_IN_IFRAME,
  OKTA_SCOPES,
} from "@src/env";
import { navigationV2 } from "@src/shared/navigation/v2";
import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";

import { authClient } from "../../data";
import { startMobileOktaLogin } from "./useMobileOktaHandoff";

function buildCallbackUrl(redirectTo: string | null): string {
  const callbackUrl = new URL(
    navigationV2.loginCallback(),
    window.location.origin,
  );

  if (redirectTo) {
    callbackUrl.searchParams.set("redirectTo", redirectTo);
  }

  return callbackUrl.toString();
}

export function useLoginWithElationFlow() {
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const enabled = !IS_CHROME_EXTENSION && !IS_IN_IFRAME;
  const redirectTo = searchParams.get("redirectTo");

  const login = async () => {
    if (!enabled) return;

    setIsSubmitting(true);
    setError(null);

    try {
      if (IS_CAPACITOR) {
        // Okta's hosted login can't complete inside the app's own WebView —
        // hand off to the system browser instead. See useMobileOktaHandoff.
        // Unlike the web path (which navigates the whole page away to Okta),
        // opening the system browser leaves this login screen mounted, so the
        // submit state must be released — the deep-link listener drives the
        // rest of the flow from here.
        try {
          await startMobileOktaLogin();
        } catch (err) {
          // Track under okta_mobile — falling through to the outer catch would
          // mislabel a mobile handoff-launch failure as okta_web.
          const errorMsg = toErrorMessage(err);
          setError(errorMsg);
          events.loginFailed.track({ method: "okta_mobile", error: errorMsg });
        } finally {
          setIsSubmitting(false);
        }
        return;
      }

      const callbackURL = buildCallbackUrl(redirectTo);

      await authClient.signIn.oauth2({
        providerId: "okta",
        callbackURL,
        newUserCallbackURL: callbackURL,
        scopes: OKTA_SCOPES,
      });
    } catch (err) {
      console.error("Login error:", err);

      const errorMsg = toErrorMessage(err);

      setError(errorMsg);
      events.loginFailed.track({ method: "okta_web", error: errorMsg });
      setIsSubmitting(false);
    }
  };

  return {
    enabled,
    error,
    isSubmitting,
    login,
  } as const;
}
